import type { RemoteNavItem, RemoteUserMenuItem } from '@platform-ui/remote-types';

type NavL2Item = {
    id: string;
    label: string;
    path?: string;
    children?: NavL2Item[];
};

type NavL2Section = {
    id: string;
    label?: string;
    items: NavL2Item[];
};

type NavL2Config = {
    title?: string;
    sections: NavL2Section[];
};

type RemoteNavItemWithL2 = RemoteNavItem & {
    l2?: NavL2Config;
};

function normalizePathWithBase(path: string | undefined, basePath: string): string {
    if (!path) {
        return !basePath || basePath === '/' ? '/' : basePath;
    }

    if (!basePath || basePath === '/') {
        return path;
    }

    if (path === '/') {
        return basePath;
    }

    if (path.startsWith(basePath)) {
        return path;
    }

    return `${basePath}${path}`;
}

function normalizeL2ConfigWithBase(l2: NavL2Config, basePath: string): NavL2Config {
    return {
        ...l2,
        sections: l2.sections.map(section => ({
            ...section,
            items: section.items.map(item => ({
                ...item,
                path: normalizePathWithBase(item.path, basePath),
                children: item.children?.map(child => ({
                    ...child,
                    path: normalizePathWithBase(child.path, basePath)
                }))
            }))
        }))
    };
}

/**
 * Normalizes nav items by prepending the basePath to relative paths.
 */
export function normalizeNavItems(items: RemoteNavItem[], basePath: string): RemoteNavItem[] {
    return items.map(item => {
        const navItem = item as RemoteNavItemWithL2;
        const fullPath = normalizePathWithBase(navItem.path, basePath);
        const normalizedItem: RemoteNavItemWithL2 = {
            ...navItem,
            path: fullPath,
            l2: navItem.l2 ? normalizeL2ConfigWithBase(navItem.l2, basePath) : navItem.l2
        };
        return normalizedItem;
    });
}

/**
 * Normalizes user menu items by prepending the basePath to relative paths.
 * Items with externalUrl or no path are returned unchanged.
 */
export function normalizeUserMenuItems(items: RemoteUserMenuItem[], basePath: string): RemoteUserMenuItem[] {
    return items.map(item => {
        // Items without a path (e.g., externalUrl only) don't need normalization
        if (!item.path) {
            return item;
        }

        const fullPath = normalizePathWithBase(item.path, basePath);
        return { ...item, path: fullPath };
    });
}

/**
 * Calculate the subpath from a full pathname given a basePath.
 * Handles the special case where basePath is '/' (root app).
 */
export function getSubpath(pathname: string, basePath: string): string {
    if (basePath === '/') {
        return pathname;
    }
    if (pathname.startsWith(basePath)) {
        return pathname.slice(basePath.length) || '/';
    }
    return '/';
}
